import { supabase } from "./supabase";

const IMAGE_BUCKET = "public-images";

// Resolve a stored image reference to a displayable URL.
// - Full URLs (Supabase Storage public URLs, external images) pass through.
// - Local paths like "/images/foo.jpeg" are served statically from /public.
export const mediaUrl = (p) => {
  if (!p) return "";
  return p;
};

export function formatApiError(err) {
  if (!err) return "Something went wrong. Please try again.";
  if (typeof err === "string") return err;
  if (err.message) return err.message;
  return String(err);
}

/* ----------------------------- Auth ----------------------------- */
export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data.user;
}

export async function signOut() {
  await supabase.auth.signOut();
}

export async function sendPasswordResetEmail(email, redirectTo) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
  if (error) throw error;
}

export async function updateAdminPassword(newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

/* ------------------------ Cloudflare rebuild -------------------- */
// Fires a Cloudflare Pages deploy hook so prerendered pages + the sitemap
// pick up new content. Safe to call: no-op when the hook URL is not
// configured. Failures are logged but never surface to the admin — the
// content change itself already succeeded in Supabase.
export async function triggerRebuild(reason = "content updated") {
  const url = process.env.REACT_APP_CLOUDFLARE_DEPLOY_HOOK;
  if (!url) {
    console.info("[rebuild] Skipped (REACT_APP_CLOUDFLARE_DEPLOY_HOOK not set):", reason);
    return { skipped: true };
  }
  try {
    await fetch(url, { method: "POST", mode: "no-cors" });
    return { ok: true };
  } catch (err) {
    console.warn("[rebuild] Deploy hook call failed:", err);
    return { ok: false, error: err };
  }
}

/* --------------------------- Content ---------------------------- */
export async function getContent() {
  const { data, error } = await supabase
    .from("content")
    .select("data")
    .eq("id", 1)
    .single();
  if (error) throw error;
  return data.data;
}

export async function saveContent(content) {
  const { data, error } = await supabase
    .from("content")
    .update({ data: content, updated_at: new Date().toISOString() })
    .eq("id", 1)
    .select("data")
    .single();
  if (error) throw error;
  return data.data;
}

/* ---------------------------- Blog ------------------------------ */
function slugify(title) {
  return (
    (title || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "post"
  );
}

async function uniqueSlug(title) {
  const base = slugify(title);
  const { data } = await supabase.from("blog_posts").select("slug").ilike("slug", `${base}%`);
  const taken = new Set((data || []).map((r) => r.slug));
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}-${i}`)) i += 1;
  return `${base}-${i}`;
}

// Public: published posts, with optional search + category filter.
export async function listPublishedPosts({ search, category } = {}) {
  let q = supabase
    .from("blog_posts")
    .select("*")
    .eq("status", "published")
    .order("published_at", { ascending: false });
  if (category && category !== "All") q = q.eq("category", category);
  if (search) q = q.or(`title.ilike.%${search}%,excerpt.ilike.%${search}%`);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function listCategories() {
  const { data, error } = await supabase
    .from("blog_posts")
    .select("category")
    .eq("status", "published");
  if (error) return [];
  return [...new Set((data || []).map((r) => r.category).filter(Boolean))];
}

export async function getPostBySlug(slug) {
  const { data, error } = await supabase
    .from("blog_posts")
    .select("*")
    .eq("slug", slug)
    .eq("status", "published")
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("Post not found");
  return data;
}

// Admin: all posts (drafts + published).
export async function listAllPosts() {
  const { data, error } = await supabase
    .from("blog_posts")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getPostById(id) {
  const { data, error } = await supabase.from("blog_posts").select("*").eq("id", id).single();
  if (error) throw error;
  return data;
}

export async function createPost(p) {
  const slug = await uniqueSlug(p.title);
  const now = new Date().toISOString();
  const row = {
    title: p.title,
    slug,
    excerpt: p.excerpt || "",
    category: p.category || "General",
    tags: p.tags || [],
    cover_image: p.cover_image || null,
    body_html: p.body_html || "",
    status: p.status || "draft",
    published_at: p.status === "published" ? now : null,
    seo_title: p.seo_title || null,
    meta_description: p.meta_description || null,
  };
  const { data, error } = await supabase.from("blog_posts").insert(row).select().single();
  if (error) throw error;
  return data;
}

export async function updatePost(id, p) {
  const patch = {
    title: p.title,
    excerpt: p.excerpt || "",
    category: p.category || "General",
    tags: p.tags || [],
    cover_image: p.cover_image || null,
    body_html: p.body_html || "",
    status: p.status || "draft",
    updated_at: new Date().toISOString(),
    seo_title: p.seo_title || null,
    meta_description: p.meta_description || null,
  };
  if (p.status === "published") patch.published_at = p.published_at || new Date().toISOString();
  const { data, error } = await supabase
    .from("blog_posts")
    .update(patch)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deletePost(id) {
  const { error } = await supabase.from("blog_posts").delete().eq("id", id);
  if (error) throw error;
}

/* --------------------------- Storage ---------------------------- */
export async function uploadImage(file) {
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
  const path = `uploads/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage
    .from(IMAGE_BUCKET)
    .upload(path, file, { cacheControl: "3600", upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from(IMAGE_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}
